import { groupMeta, theme } from '../constants';
import { deviceKey, groupDevicesByRoom } from '../function';
import { CheckIcon, DeviceGlyph, SearchIcon } from './Icons';

const EmptyState = ({ title, subtitle }) => (
  <div style={{ padding: '48px 20px', textAlign: 'center' }}>
    <div style={{ fontSize: 13.5, fontWeight: 700, color: theme.textMuted }}>{title}</div>
    {subtitle && <div style={{ fontSize: 12.5, color: theme.textFaint, marginTop: 5 }}>{subtitle}</div>}
  </div>
);

const Skeletons = () => (
  <div style={{ padding: '6px 2px', display: 'flex', flexDirection: 'column', gap: 10 }}>
    {[0, 1, 2, 3, 4].map((row) => (
      <div key={row} className="dmSkeleton" style={{ height: 52, opacity: 1 - row * 0.15 }} />
    ))}
  </div>
);

function TypeChips({ counts, active, onChange }) {
  const chips = [{ key: 'all', label: 'All', color: theme.text, count: counts.total }, ...counts.groups];

  return (
    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
      {chips.map((chip) => {
        const isActive = active === chip.key;
        return (
          <button
            key={chip.key}
            onClick={() => onChange(chip.key)}
            style={{
              padding: '5px 11px',
              borderRadius: 999,
              fontSize: 12,
              fontFamily: theme.font,
              fontWeight: isActive ? 700 : 600,
              cursor: 'pointer',
              transition: 'all 0.15s',
              border: `1px solid ${isActive ? chip.color : theme.border}`,
              background: isActive ? `${chip.color}14` : theme.surface,
              color: isActive ? chip.color : theme.textMuted,
            }}
          >
            {chip.label}
            <span style={{ marginLeft: 5, opacity: 0.65, fontWeight: 600 }}>{chip.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function DeviceRow({ device, selected, onSelect }) {
  const meta = groupMeta(device.groupKey);

  return (
    <div
      className="dmDeviceRow"
      role="checkbox"
      aria-checked={selected}
      tabIndex={0}
      onClick={() => onSelect(device)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(device);
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 14px',
        borderRadius: 10,
        cursor: 'pointer',
        outline: 'none',
        border: `1.5px solid ${selected ? theme.accent : theme.border}`,
        background: selected ? theme.accentSoft : theme.surface,
        boxShadow: selected ? `0 1px 8px ${theme.accentSoft}` : 'none',
      }}
    >
      <span
        style={{
          width: 17,
          height: 17,
          borderRadius: 5,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1.5px solid ${selected ? theme.accent : '#d1d5db'}`,
          background: selected ? theme.accent : theme.surface,
          color: '#fff',
        }}
      >
        {selected && <CheckIcon size={11} />}
      </span>

      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 9,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: meta.bg,
          color: meta.color,
        }}
      >
        <DeviceGlyph groupKey={device.groupKey} size={16} />
      </span>

      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            fontSize: 13.5,
            fontWeight: 700,
            color: theme.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {device.name}
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: theme.textFaint, marginTop: 1 }}>
          {device.hasName ? device.deviceId : 'Unnamed device'}
          {device.applianceCount > 0 && ` · ${device.applianceCount} appliances`}
        </span>
      </span>

      <span
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          padding: '3px 8px',
          borderRadius: 6,
          flexShrink: 0,
          background: meta.bg,
          color: meta.color,
        }}
      >
        {meta.label}
      </span>
    </div>
  );
}

/**
 * Step 2 — searchable, family-filtered, room-grouped picker for the
 * device(s) being moved. Multiple devices can be selected at once.
 */
function DeviceList({
  devices,
  counts,
  loading,
  hasHouse,
  search,
  onSearchChange,
  typeFilter,
  onTypeChange,
  selectedKeys,
  onSelect,
  onToggleSelectAll,
}) {
  const rooms = groupDevicesByRoom(devices);
  const allVisibleSelected =
    devices.length > 0 && devices.every((device) => selectedKeys.includes(deviceKey(device)));

  const renderBody = () => {
    if (!hasHouse) {
      return <EmptyState title="Pick the current property first" subtitle="Its devices will be listed here, grouped by room." />;
    }
    if (loading) return <Skeletons />;
    if (!counts.total) {
      return <EmptyState title="No devices on this property" subtitle="Choose a different property to move a device from." />;
    }
    if (!devices.length) {
      return <EmptyState title="No devices match this filter" subtitle="Try a different search term or device type." />;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {rooms.map((room) => (
          <div key={room.roomName}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
                fontSize: 11.5,
                fontWeight: 800,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: theme.textFaint,
              }}
            >
              {room.roomName}
              <span style={{ flex: 1, height: 1, background: theme.border }} />
              <span style={{ letterSpacing: 0 }}>{room.devices.length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {room.devices.map((device) => (
                <DeviceRow
                  key={deviceKey(device)}
                  device={device}
                  selected={selectedKeys.includes(deviceKey(device))}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      style={{
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 12,
        padding: '16px 18px 18px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: 7,
              background: theme.accentSoft,
              color: theme.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11.5,
              fontWeight: 800,
            }}
          >
            2
          </span>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: theme.text, lineHeight: '17px' }}>Select Device</div>
            <div style={{ fontSize: 11.5, color: theme.textFaint, lineHeight: '16px' }}>Select one or more devices</div>
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <span
            style={{
              position: 'absolute',
              left: 11,
              top: '50%',
              transform: 'translateY(-50%)',
              color: theme.textFaint,
              pointerEvents: 'none',
              display: 'flex',
            }}
          >
            <SearchIcon size={14} />
          </span>
          <input
            type="text"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search name, ID or room…"
            disabled={!hasHouse}
            style={{
              border: `1px solid ${theme.border}`,
              borderRadius: 8,
              padding: '7px 12px 7px 32px',
              fontSize: 13,
              fontFamily: theme.font,
              outline: 'none',
              width: 250,
              color: '#374151',
              background: hasHouse ? theme.surface : theme.surfaceMuted,
            }}
          />
        </div>
      </div>

      {hasHouse && counts.total > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 14,
          }}
        >
          <TypeChips counts={counts} active={typeFilter} onChange={onTypeChange} />
          {devices.length > 0 && (
            <button
              onClick={onToggleSelectAll}
              style={{
                border: 'none',
                background: 'transparent',
                color: theme.accent,
                fontFamily: theme.font,
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                padding: '4px 2px',
                whiteSpace: 'nowrap',
              }}
            >
              {allVisibleSelected ? 'Clear all' : `Select all (${devices.length})`}
            </button>
          )}
        </div>
      )}

      <div className="dmScroll" style={{ maxHeight: 380, overflowY: 'auto', paddingRight: 4 }}>
        {renderBody()}
      </div>
    </div>
  );
}

export default DeviceList;
